import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, X } from 'lucide-react';
import { applyServiceWorkerUpdate, onServiceWorkerUpdate, isBlockedContext, getSwPhase, type SwPhase } from '@/pwa/registerServiceWorker';
import {
  acquireUpdateWindow,
  getAppBusyState,
  onBusyChange,
  releaseUpdateWindow,
  type BusyState,
} from '@/pwa/busyRegistry';
import {
  confirmUpdateAfterLoad,
  getPendingUpdate,
  markUpdateAttempt,
  getSnoozedUntil,
  snoozeUpdate,
} from '@/pwa/updateState';
import { toast } from 'sonner';

/**
 * Banner "Nova versão disponível". Nunca recarrega sozinho: a atualização
 * só acontece quando o usuário clica — e é adiada se houver trabalho em
 * andamento (ou dúvida sobre alguma aba) no aplicativo.
 */
export function PwaUpdatePrompt() {
  const [visible, setVisible] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [busyState, setBusyState] = useState<BusyState>(getAppBusyState());
  const [swPhase, setSwPhase] = useState<SwPhase>(getSwPhase());

  useEffect(() => {
    const onPhase = () => setSwPhase(getSwPhase());
    window.addEventListener('autofluxo:sw-status', onPhase);
    return () => window.removeEventListener('autofluxo:sw-status', onPhase);
  }, []);

  useEffect(() => {
    if (isBlockedContext()) return;
    confirmUpdateAfterLoad();
    const refresh = () => setVisible(!!getPendingUpdate() && Date.now() >= getSnoozedUntil());
    const offWorker = onServiceWorkerUpdate(refresh);
    refresh();
    const timer = window.setInterval(refresh, 1000);
    window.addEventListener('autofluxo:update-available', refresh);
    window.addEventListener('autofluxo:update-status', refresh);
    window.addEventListener('storage', refresh);
    const offBusy = onBusyChange(setBusyState);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('autofluxo:update-available', refresh);
      window.removeEventListener('autofluxo:update-status', refresh);
      window.removeEventListener('storage', refresh);
      offWorker();
      offBusy();
    };
  }, []);

  if (!visible) return null;

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      // Verificação final imediatamente antes de aplicar, com trava para
      // não abrir janela de corrida com uma operação recém-iniciada.
      let state = await acquireUpdateWindow();
      // iPhone: a aba suspensa costuma responder ao voltar; tenta de novo antes de desistir.
      if (state === 'unknown') {
        await new Promise((r) => setTimeout(r, 2000));
        state = await acquireUpdateWindow();
      }

      if (state === 'busy') {
        toast.warning('Há algo em andamento', {
          description: 'Conclua ou salve o que está aberto e toque em Atualizar novamente.',
        });
        return;
      }
      if (state === 'unknown') {
        toast.warning('Não foi possível confirmar as outras abas', {
          description:
            'Feche as outras abas do AutoFluxo (ou traga-as para a frente) e toque em Atualizar novamente.',
        });
        return;
      }

      const pending = getPendingUpdate();
      if (pending) markUpdateAttempt(pending);

      try {
        await applyServiceWorkerUpdate(pending || undefined);
        // Se chegou aqui sem recarregar, nada foi confirmado: a pendência
        // permanece e o aviso continua visível para nova tentativa.
      } catch {
        toast.error('Não foi possível atualizar agora', {
          description: 'Tente novamente em alguns instantes.',
        });
      }
    } finally {
      releaseUpdateWindow();
      setUpdating(false);
    }
  };

  const defer = () => { snoozeUpdate(); setVisible(false); };

  return (
    <div className="fixed bottom-4 left-1/2 z-[320] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-xl border border-border bg-card p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <RefreshCw className={`mt-0.5 h-5 w-5 shrink-0 text-primary ${updating ? 'animate-spin' : ''}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Nova versão disponível</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {busyState === 'busy'
              ? 'Você tem algo em andamento. Conclua e depois atualize.'
              : busyState === 'unknown'
                ? 'Há outra aba do AutoFluxo que não respondeu. Verifique antes de atualizar.'
                : 'Atualize para receber as últimas melhorias do AutoFluxo.'}
          </p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={handleUpdate} disabled={updating}>
              {updating ? (swPhase === 'installing' ? 'Baixando atualização…' : 'Atualizando...') : 'Atualizar agora'}
            </Button>
            <Button size="sm" variant="ghost" onClick={defer} disabled={updating}>
              Depois
            </Button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Fechar"
          onClick={defer} disabled={updating}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
